// src/lib/qualityServices.ts
import { supabase } from './supabaseClient';

export type QualityServiceType = 'mystery_client' | 'customer_survey' | 'combined';
export type QualityRequestStatus = 'draft' | 'submitted' | 'in_review' | 'scheduled' | 'completed' | 'cancelled';

export type QualityServiceRequestPayload = {
  business_id?: string | null;
  business_name?: string;
  contact_name?: string;
  contact_email: string;
  contact_phone?: string;
  service_type: QualityServiceType;
  region?: string;
  preferred_period?: string;
  visit_goal?: string;
  survey_goal?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
};

export type QualityServiceRequest = QualityServiceRequestPayload & {
  id: string;
  status: QualityRequestStatus;
  created_at: string;
  updated_at?: string;
};

export type QualityDashboardMetrics = {
  requests_total: number;
  requests_pending: number;
  requests_completed: number;
  average_score: number | null;
  latest_reports: QualityReportSummary[];
};

export type QualityReportSummary = {
  id: string;
  business_id?: string | null;
  business_name?: string | null;
  report_type: string;
  score_overall?: number | null;
  status: string;
  created_at: string;
};

export async function submitQualityServiceRequest(payload: QualityServiceRequestPayload) {
  const { data, error } = await supabase.functions.invoke('quality-services', {
    body: { action: 'create_request', ...payload },
  });

  if (error) throw error;
  if (!data?.ok) throw new Error(data?.error || 'quality_request_failed');
  return data;
}

export async function getMerchantQualityDashboard(businessId?: string | null) {
  const { data, error } = await supabase.functions.invoke('quality-services', {
    body: { action: 'merchant_dashboard', business_id: businessId || null },
  });

  if (error) throw error;
  if (!data?.ok) throw new Error(data?.error || 'quality_dashboard_failed');
  return data as { ok: true; metrics: QualityDashboardMetrics; requests: QualityServiceRequest[]; reports: QualityReportSummary[] };
}

export async function adminQualityAction(action: string, payload: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke('admin-quality-services', {
    body: { action, ...payload },
  });

  if (error) throw error;
  if (!data?.ok) throw new Error(data?.error || 'admin_quality_action_failed');
  return data;
}

export function qualityServiceLabel(type?: string | null) {
  switch (type) {
    case 'mystery_client':
      return 'Client mystère';
    case 'customer_survey':
      return 'Sondage client';
    case 'combined':
      return 'Client mystère + sondage';
    default:
      return 'Service qualité';
  }
}

export function qualityStatusLabel(status?: string | null) {
  switch (status) {
    case 'submitted':
      return 'Soumis';
    case 'in_review':
      return 'Analyse Forfeo';
    case 'scheduled':
      return 'Planifié';
    case 'completed':
      return 'Complété';
    case 'cancelled':
      return 'Annulé';
    case 'draft':
      return 'Brouillon';
    default:
      return status || 'Inconnu';
  }
}
